import { ArrowDownIcon, ArrowUpIcon } from "@xola/icons";
import clsx from "clsx";
import React from "react";
import type { InventoryStats } from "./adjustInventoryTypes";

interface InventoryStatsGridProps {
    available: number;
    unavailable: number;
    committed: number;
    onHand: number;
    baseline?: InventoryStats;
}

export const InventoryStatsGrid = ({
    available,
    unavailable,
    committed,
    onHand,
    baseline,
}: InventoryStatsGridProps) => (
    <div className="grid grid-cols-4 gap-3 px-8">
        <StatTile label="Available" value={available} delta={baseline && getDelta(baseline.available, available)} />
        <StatTile
            label="Unavailable"
            value={unavailable}
            delta={baseline && getDelta(baseline.unavailable, unavailable)}
        />
        <StatTile label="Committed" value={committed} />
        <StatTile label="On-Hand" value={onHand} delta={baseline && getDelta(baseline.onHand, onHand)} />
    </div>
);

const getDelta = (baselineValue: number, currentValue: number): number => currentValue - baselineValue;

interface StatTileProps {
    label: string;
    value: number;
    delta?: number;
}

const StatTile = ({ label, value, delta }: StatTileProps) => (
    <div
        className="flex h-20 flex-col items-center justify-center rounded border border-gray-lighter p-3 text-center"
        data-testid={`inventory-stat-${label}`}
    >
        <span className="text-gray-900 mb-1 font-bold">{label}</span>
        <div className="grid w-full grid-cols-[3.5rem_1fr_3.5rem] items-center">
            <span />
            <span className="text-gray-900 text-2xl font-bold">{value}</span>
            <span className="text-left">{!!delta && <DeltaBadge delta={delta} />}</span>
        </div>
    </div>
);

interface DeltaBadgeProps {
    delta: number;
}

const DeltaBadge = ({ delta }: DeltaBadgeProps) => {
    const isIncrease = delta > 0;
    const colorClass = isIncrease ? "text-green" : "text-red";
    const Icon = isIncrease ? ArrowUpIcon : ArrowDownIcon;

    return (
        <span className={clsx("inline-flex items-center gap-0.5 text-sm font-bold", colorClass)}>
            (<Icon size="small" />
            {delta})
        </span>
    );
};
